<template>
    <div>
        <div class="grid-fitted stack-m">
            <h3 class="grid-item">document analysis</h3>
            <span class="grid-item no-flex text-muted">Created on {{document.createdTime | dateTime}}</span>
        </div>
        <div :class="{'stack-m' : matchScore}">
            <span v-if="document.hasReview">Document successfully processed on {{document.processedTime | dateTime}}</span>
            <span v-else>Document analysis is {{document.status | unKebabCase | unCapitalize}}</span>
        </div>
        <div v-if="matchScore" :class="{'stack-m' : matchFindings}">
            <span :class="getBadgeType(matchScore)">Analysis Score {{matchScoreFormatted}}</span>
        </div>
        <div v-if="matchFindings" class="grid-fitted">
            <div v-for="finding in matchFindings" class="grid-item grid-item-half">
                <div class="grid-fitted">
                    <v-icon v-bind="iconPropsState(finding)" class="no-flex"/>
                    <span class="grid-item">{{finding.message}}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import vIcon from '@/components/form/v-icon';
    import {DateTimeFormats} from '@/common/datetime-formats';

    export default {
        name: 'document-analysis',
        components: {vIcon},
        props: {
            document: {
                type: Object,
                required: true,
            },
        },
        computed: {
            matches() {
                return this.document.documentMatches;
            },
            matchScore() {
                return this.matches && this.matches.score;
            },
            matchScoreFormatted() {
                return this.matchScore
                    && this.$filters.number(this.matchScore, {style: 'percent'});
            },
            matchFindings() {
                const data = this.matches && this.matches.data;
                return data && [
                    this.findingImage(data),
                    this.findingDOB(data),
                    this.findingID(data),
                    this.findingExpirationDate(data),
                    this.findingOnline(data),
                    this.findingIssueDate(data),
                    this.findingFirstName(data),
                    this.findingAge(data),
                    this.findingLastName(data),
                ];
            },
        },
        methods: {
            getBadgeType(matchScore) {
                const badgeType = ((score) => {
                    switch (true) {
                        case (score >= 0.60):
                            return 'positive';
                        case (score >= 0.40):
                            return 'warning';
                        default:
                            return 'negative';
                    }
                })(matchScore);
                return `badge badge-${badgeType}`;
            },
            iconPropsState({isPositive}) {
                return {
                    icon: isPositive ? 'check-s' : 'close-s',
                    class: isPositive ? 'icon-green' : 'icon-red',
                };
            },
            findingImage({containsImage}) {
                return {
                    message: `This image ${containsImage ? 'contains one or more faces' : 'does not contain any faces'}`,
                    isPositive: containsImage,
                };
            },
            findingID({isIdentityDocument}) {
                return {
                    message: `This image ${isIdentityDocument ? 'looks' : 'does not look'} like an ID`,
                    isPositive: isIdentityDocument,
                };
            },
            findingOnline({isPublishedOnline}) {
                return {
                    message: `Document was ${isPublishedOnline ? 'found' : 'not found'} online`,
                    isPositive: !isPublishedOnline,
                };
            },
            findingFirstName({firstName}) {
                return {
                    message: `First name ${firstName ? `match ${firstName}` : 'does not match'}`,
                    isPositive: firstName,
                };
            },
            findingLastName({lastName}) {
                return {
                    message: `Last name ${lastName ? `match ${lastName}` : 'does not match'}`,
                    isPositive: lastName,
                };
            },
            findingDOB({dateOfBirth}) {
                return {
                    message: `Date of birth${this.findingDate(dateOfBirth)}`,
                    isPositive: dateOfBirth,
                };
            },
            findingExpirationDate({expiryDate}) {
                let isPositive = !!expiryDate;
                if (expiryDate) {
                    const expiry = this.$tz().readUTC(expiryDate);
                    const createdTime = this.$tz().readUTC(this.document.createdTime);
                    isPositive = expiry.isValid() && expiry.isAfter(createdTime);
                }
                return {
                    message: `Expiry date${this.findingDate(expiryDate)}`,
                    isPositive,
                };
            },
            findingIssueDate({issueDate}) {
                return {
                    message: `Issue date${this.findingDate(issueDate)}`,
                    isPositive: issueDate,
                };
            },
            findingAge({hasMinimalAge}) {
                return {
                    message: `${hasMinimalAge ? 'Subject has minimum legal age' : 'Minimum legal age unknown'}`,
                    isPositive: hasMinimalAge,
                };
            },
            findingDate(date) {
                return `${date ? `, ${this.$filters.dateFormat(date, DateTimeFormats.date)}` : ' was not found'}`;
            },
        },
    };
</script>
